/**
 * Runtime tenant branding for the supplier portal (multi-tenant SaaS migration).
 *
 * ONE build serves every tenant. At boot we resolve the tenant, fetch its PUBLIC config from the
 * platform, and paint its branding (see applyBranding.ts). The payload is display-only (name, colors,
 * logo URLs, feature toggles) and never carries secrets.
 *
 * Tenant resolution chain (first match wins) — this is what lets a NON-Dumyah tenant run, not just
 * Dumyah:
 *   1. `?tenant=<code>` query param   — ad-hoc, for local/staging testing of a specific tenant
 *   2. `REACT_APP_TENANT` build env   — a per-deploy default
 *   3. `hisbah.<tenant>.<domain>`     — the fleet host (D5): the SECOND label is the tenant
 *   4. the hostname's first label     — prod by domain (e.g. supplier.acme.com → "acme")
 *   5. "dumyah"                       — safe fallback
 *
 * Best-effort: if the fetch fails we keep the build-time look (the bundled Dumyah logo + the Metronic
 * theme), so the portal never blanks out.
 */

import {runtimeApiBase} from './apiBase'

export type TenantBrandingPayload = {
  tenant?: {code?: string | null; name?: string | null; name_ar?: string | null} | null
  phone_validation?: {
    iso_code_2?: string | null
    country?: string | null
    country_ar?: string | null
    dial_code?: string | null
    countries?: Array<{
      iso_code_2: string
      dial_code: string
      country: string
      country_ar?: string | null
      pattern: string
      prefix_pattern: string
      min_length: number
      max_length: number
      format: string
      example?: string
    }>
  } | null
  branding?: {
    colors?: Record<string, string | null | undefined> | null
    logos?: {
      desktop?: {en?: string | null; ar?: string | null} | null
      mobile?: {en?: string | null; ar?: string | null} | null
    } | null
    favicon?: string | null
  } | null
  features?: Record<string, boolean | null | undefined> | null
  /**
   * The store's money: `base_currency` is the code its amounts are recorded in (a supplier's sales,
   * fees and payments), `currencies` the codes it offers with the symbol it prints for each.
   */
  locale?: {
    default_country?: {code?: string | null} | null
    default_currency?: string | null
    base_currency?: string | null
    currencies?: Array<{
      code?: string | null
      symbol?: string | null
      symbol_left?: string | null
      symbol_right?: string | null
    }> | null
  } | null
  /**
   * Per-tenant supplier-app store links, surfaced on the Download-App page. Absent for tenants that
   * haven't published a supplier app yet — the page then hides the store buttons rather than leaking
   * Dumyah's own links. (Emitted by the platform tenant-config once the diwan-side field lands; until
   * then it's simply undefined and only Dumyah shows its baked-in defaults.)
   */
  download_app?: {ios_url?: string | null; android_url?: string | null} | null
  /**
   * Per-section "how-to" tutorial videos shown as the "Video Link" buttons across the portal. These
   * are Dumyah's own YouTube walkthroughs, so they're OFF by default for white-label tenants:
   *   - `enabled` absent  ⇒ only Dumyah shows them (see tenantTutorialsEnabled)
   *   - `links[section]`  ⇒ a tenant's own per-section video (overrides the baked-in Dumyah one)
   * A tenant that enables tutorials but hasn't supplied a link for a section simply hides that button.
   */
  tutorials?: {enabled?: boolean | null; links?: Record<string, string | null | undefined> | null} | null
}

/** The safe fallback tenant — a build with no resolvable tenant behaves exactly like plain Dumyah. */
export const DEFAULT_TENANT = 'dumyah'
const RESERVED_HOST_LABELS = new Set(['www', 'app', 'supplier', 'suppliers', 'hisbah', 'hisbeh', 'staging', 'localhost', 'local-hisbeh'])

/** Resolve the active tenant code (see chain above). Pure + synchronous. */
export function resolveTenant(): string {
  try {
    const q = new URLSearchParams(window.location.search).get('tenant')
    if (q && q.trim()) return q.trim()
  } catch {
    /* no window */
  }
  const envTenant = process.env.REACT_APP_TENANT?.trim()
  if (envTenant) return envTenant
  try {
    const labels = window.location.hostname.toLowerCase().split('.')
    // Fleet host `hisb{a,e}h.<tenant>.<domain>` (D5) — the SECOND label is the tenant, checked before
    // the plain first-label case below (hisbah/hisbeh are RESERVED first labels).
    if (labels[0] === 'hisbah' || labels[0] === 'hisbeh') {
      const tenantLabel = labels[1]
      return tenantLabel && !RESERVED_HOST_LABELS.has(tenantLabel) ? tenantLabel : DEFAULT_TENANT
    }
    const label = labels[0]
    if (label && !RESERVED_HOST_LABELS.has(label)) return label
  } catch {
    /* ignore */
  }
  return DEFAULT_TENANT
}

/** Public tenant-config URL, derived from the runtime API base (overridable via REACT_APP_TENANT_CONFIG_URL). */
function tenantConfigUrl(tenant: string): string {
  const explicit = process.env.REACT_APP_TENANT_CONFIG_URL?.trim()
  // No localhost fallback: runtimeApiBase() already fails safe to same-origin, and a hardcoded
  // localhost default here would point every tenant's branding fetch at whichever machine loaded
  // the page — the same leak deploy-fleet.yml exists to prevent. An empty base yields a
  // same-origin relative URL, which is the correct behaviour for the one shared build.
  const apiBase = runtimeApiBase().replace(/\/$/, '')
  const base = (explicit || `${apiBase}/api/v1/tenant-config`).replace(/\/$/, '')
  const sep = base.includes('?') ? '&' : '?'
  return `${base}${sep}tenant=${encodeURIComponent(tenant)}`
}

/** Lumen wraps success bodies as `{ data, response_code }`; unwrap transparently when present. */
function unwrapEnvelope(value: unknown): unknown {
  if (value && typeof value === 'object' && 'data' in value && 'response_code' in value) {
    return (value as {data: unknown}).data
  }
  return value
}

/**
 * Fetch the active tenant's public config. Rejects on network/non-2xx so the caller falls back to the
 * build-time look.
 *
 * Deliberately UNCREDENTIALED, and it must stay that way. The endpoint is public — dumyah registers
 * `GET tenant-config` in `lumen/routes/v1/common.php` with NO middleware, and
 * `v1/TenantConfigController::show()` resolves the tenant from the pool-pinned `config('brand.code')`,
 * never from a session cookie. Meanwhile every dumyah vhost answers with
 * `Access-Control-Allow-Origin: *` (nginx is the declared single CORS authority and hardcodes the
 * wildcard, hiding Lumen's origin-aware header). A wildcard ACAO is a HARD rejection for a
 * credentialed request, so `credentials: 'include'` here made the branding fetch fail on every
 * cross-origin host — i.e. all of them — and every white-label tenant silently kept the bundled
 * Dumyah look. Without the flag this is a plain simple GET that `*` satisfies everywhere.
 */
export async function fetchTenantConfig(signal?: AbortSignal): Promise<TenantBrandingPayload> {
  const res = await fetch(tenantConfigUrl(resolveTenant()), {
    method: 'GET',
    headers: {Accept: 'application/json'},
    signal,
  })
  if (!res.ok) throw new Error(`tenant-config ${res.status}`)
  return unwrapEnvelope(await res.json()) as TenantBrandingPayload
}
